import { useState, useEffect } from 'react'
import { MdFlight, MdAdd, MdSchedule, MdRefresh, MdWarning } from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  LoadingSpinner,
  SearchBar,
  FilterBar,
  StatCard,
  EmptyState,
} from '../../components/ui'
import { useAirport } from '../../hooks/useAirport'
import { useWebsocket } from '../../hooks/useWebsocket'
import AirportList from '../../modules/airports/AirportList'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function AirportPage() {
  const dispatch = useDispatch()
  const [airports, setAirports] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    international: 0,
    domestic: 0,
  })

  const navigate = useNavigate()
  const { getAirportsAsync, isGetAirportsLoading } = useAirport()

  const loadAirports = async () => {
    try {
      setIsLoading(true)
      const airportsData = await getAirportsAsync()
      setAirports(airportsData || [])
      setStats({
        total: airportsData.length,
        active: airportsData.filter((a) => a.isActive).length,
        international: airportsData.filter((a) => a.type === 'international').length,
        domestic: airportsData.filter((a) => a.type === 'domestic').length,
      })
    } catch (error) {
      console.error('Error loading airports:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadAirports()

    const channelName = getWebsocketChannel().airportList

    if (channelName) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    return () => {
      if (channelName) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getFilteredAirports = () => {
    let filtered = airports
    if (typeFilter !== 'all') {
      if (typeFilter === 'active') filtered = filtered.filter((a) => a.isActive)
      else filtered = filtered.filter((a) => a.type === typeFilter)
    }
    if (searchTerm) {
      filtered = filtered.filter(
        (a) =>
          a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.country?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }
    return filtered
  }

  const filteredAirports = getFilteredAirports()

  const handleAddAirport = () => {
    navigate('/airports/new')
  }

  const handleRefresh = () => {
    loadAirports()
  }

  const handleViewAirport = (airport) => {
    navigate(`/airports/${airport._id}`)
  }

  const handleEditAirport = (airport) => {
    navigate(`/airports/${airport._id}/edit`)
  }

  const handleDeleteAirport = (airport) => {
    // TODO: Implement delete confirmation modal
    console.log('Delete airport:', airport._id)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
              Airport Management
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Monitor and manage airport operations and facilities
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={isGetAirportsLoading}
              className="flex items-center space-x-2"
            >
              <MdRefresh className={isGetAirportsLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </Button>
            <Button
              variant="primary"
              onClick={handleAddAirport}
              className="flex items-center space-x-2"
            >
              <MdAdd className="text-lg" />
              <span>Add Airport</span>
            </Button>
          </div>
        </div>
      </div>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Airports"
          value={stats.total}
          subtitle="Registered airports"
          icon={MdFlight}
          iconBgColor="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
        />
        <StatCard
          title="Active Airports"
          value={stats.active}
          subtitle="Currently operating"
          icon={MdSchedule}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />
        <StatCard
          title="International"
          value={stats.international}
          subtitle="International airports"
          icon={MdFlight}
          iconBgColor="bg-info-100 dark:bg-info-900"
          iconColor="text-info-600 dark:text-info-400"
        />
        <StatCard
          title="Domestic"
          value={stats.domestic}
          subtitle="Domestic airports"
          icon={MdWarning}
          iconBgColor="bg-warning-100 dark:bg-warning-900"
          iconColor="text-warning-600 dark:text-warning-400"
        />
      </div>
      {/* Filters and Search */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Airports ({filteredAirports.length})
          </h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search airports..."
            />
            <div className="flex items-center space-x-3">
              <FilterBar
                filterValue={typeFilter}
                onFilterChange={(e) => setTypeFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Airports' },
                  { value: 'active', label: `Active (${stats.active})` },
                  { value: 'international', label: `International (${stats.international})` },
                  { value: 'domestic', label: `Domestic (${stats.domestic})` },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Airports List */}
      {filteredAirports.length === 0 && !isLoading ? (
        <EmptyState
          icon={MdFlight}
          title="No airports found"
          description={
            searchTerm || typeFilter !== 'all'
              ? 'No airports match your current filters. Try adjusting your search criteria.'
              : 'Get started by adding your first airport to the system.'
          }
          action={
            searchTerm || typeFilter !== 'all' ? (
              <Button
                variant="primary"
                onClick={() => {
                  setSearchTerm('')
                  setTypeFilter('all')
                }}
              >
                Clear Filters
              </Button>
            ) : (
              <Button variant="primary" onClick={handleAddAirport}>
                Add Airport
              </Button>
            )
          }
        />
      ) : (
        <AirportList
          airports={filteredAirports}
          onViewAirport={handleViewAirport}
          onEditAirport={handleEditAirport}
          onDeleteAirport={handleDeleteAirport}
        />
      )}
    </div>
  )
}

export default AirportPage
